import { t } from 'i18next'
import React, { FunctionComponent, useEffect, useState } from 'react'

import { ClinTheme } from '../../../ClinTheme'
import { ClinButton } from '../../../components/ClinButton'
import { ClinGroup } from '../../../components/ClinGroup'
import { ClinModal } from '../../../components/ClinModal'
import { ClinSpacer } from '../../../components/ClinSpacer'
import { ClinSpinner } from '../../../components/ClinSpinner'
import { ClinText } from '../../../components/ClinText'
import { TypographyVariant } from '../../../components/ClinText/ClinText.styles'
import { updatePatient } from '../../../services/ApiService'
import { PatientOrderStatus } from '../PatientDashboard/Patient.model'

export interface IMissingDraftOrderModalProps {
  isOpen: boolean
  /** Still looking for the draft order in the orders search. */
  isLookingUp?: boolean
  /** The patient's own physician (not the order line's). */
  physicianId?: string
  patientId?: string | number
  onClose: () => void
  /** Called once the patient is back on Begin your order. */
  onStartedAgain: () => void
}

/**
 * CLOS-14565: the patient is on One more step to go but the draft order is
 * missing (no order number, or the order no longer exists). Explain it and let
 * the user start again, which returns the patient to Begin your order.
 */
export const MissingDraftOrderModal: FunctionComponent<
  IMissingDraftOrderModalProps
> = ({
  isOpen,
  isLookingUp = false,
  physicianId,
  patientId,
  onClose,
  onStartedAgain
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setHasError(false)
    }
  }, [isOpen])

  const handleStartAgain = () => {
    if (!physicianId || !patientId) {
      return
    }
    setIsSubmitting(true)
    setHasError(false)
    updatePatient(physicianId, patientId, {
      orderStatus: PatientOrderStatus.BeginOrder
    })
      .then(() => {
        setIsSubmitting(false)
        onStartedAgain()
      })
      .catch(() => {
        setIsSubmitting(false)
        setHasError(true)
      })
  }

  if (isLookingUp) {
    return (
      <ClinModal isOpen={isOpen} isDismissible={false} onClose={onClose}>
        <ClinGroup alignItems="center">
          <ClinSpinner size={ClinTheme.space[4]} />
          <ClinText variant={TypographyVariant.LargeParagraph}>
            {t('patient_detail:missing_draft_order_modal.looking_up')}
          </ClinText>
        </ClinGroup>
      </ClinModal>
    )
  }

  return (
    <ClinModal isOpen={isOpen} isDismissible={false} onClose={onClose}>
      <ClinText variant={TypographyVariant.LargeParagraph}>
        {t('patient_detail:missing_draft_order_modal.description')}
      </ClinText>
      {hasError && (
        <ClinText
          color={ClinTheme.colors.redInvalid}
          marginTop={ClinTheme.space[2]}
        >
          {t('patient_detail:missing_draft_order_modal.error')}
        </ClinText>
      )}
      <ClinSpacer height={ClinTheme.space[4]} />
      <ClinGroup justifyContent="flex-end">
        <ClinButton
          variant="secondary"
          disabled={isSubmitting}
          onClick={onClose}
        >
          {t('patient_detail:missing_draft_order_modal.cancel_button_text')}
        </ClinButton>
        <ClinButton
          variant="primary"
          isLoading={isSubmitting}
          disabled={isSubmitting || !physicianId || !patientId}
          onClick={handleStartAgain}
        >
          {t(
            'patient_detail:missing_draft_order_modal.start_again_button_text'
          )}
        </ClinButton>
      </ClinGroup>
    </ClinModal>
  )
}
